import React from 'react';
import cx from 'classnames';
import { eventClickHandler as prematchEventClickHandler } from 'helpers/prematch';

import {
  groupMarketTypeInstructions,
  SelectedSportbookCategoryProvider,
  SelectedSportsbookTournamentProvider,
  SiteConfigManager,
  useSportState,
  useTournamentState,
} from '@atlas/core';

import MarketTypeSelector from 'common/MarketTypeSelector/MarketTypeSelector';
import TournamentLoader from 'common/Widgets/Tournament/TournamentLoader';
import useIsSwipeGrid from 'common/Widgets/Tournament/useIsSwipeGrid';
import { SelectedProviderWrapper } from 'components/Pages/SelectedWrappers';
import useSelectedMarketType from 'hooks/useSelectedMarketType';

import { TournamentScrollGrid } from '../TournamentScrollGrid';
import { TournamentSwipeGrid } from '../TournamentSwipeGrid';

import MarketTypeSelectorLoader from './MarketTypeSelectorLoader';
import TournamentHeader from './TournamentHeader';
import TournamentHeaderLoader from './TournamentHeaderLoader';
import TournamentRedirect from './TournamentRedirect';

const TournamentView = () => {
  const { sportForm = {} } = useTournamentState();
  const { mainTypes } = sportForm;

  const { mainMarketTypes } = useSportState();
  const mainMarketsChoice = SiteConfigManager.getConfig(
    'config.config.sportsbook.mainMarketsChoice',
  );
  const selectedMarketType = useSelectedMarketType();
  const isSwipeGrid = useIsSwipeGrid();

  const mainMarketTypesByPeriod = React.useMemo(
    () =>
      groupMarketTypeInstructions(mainMarketTypes, {
        isMultilines: !!(
          mainTypes && SiteConfigManager.getConfig('config.config.sportsbook.showMobileMultilines')
        ),
        selectedMarketType,
        tournamentMainTypes: mainTypes,
      }),
    [mainMarketTypes, mainTypes, selectedMarketType],
  );

  const className = cx('tournament', isSwipeGrid ? 'tournament-swipe' : 'tournament-scroll');

  const TournamentGridComponent = React.useMemo(() => {
    const mobileGridViewType = SiteConfigManager.getConfig(
      'config.config.sportsbook.mobileGridViewType',
    );

    if (mobileGridViewType === 'swipe') {
      return TournamentSwipeGrid;
    }

    return TournamentScrollGrid;
  }, []);

  return (
    <div className={className}>
      <div className="actions">
        <TournamentHeaderLoader>
          <TournamentHeader />
        </TournamentHeaderLoader>
        {mainMarketsChoice && (
          <MarketTypeSelectorLoader>
            <MarketTypeSelector isTournament />
          </MarketTypeSelectorLoader>
        )}
      </div>
      <TournamentLoader>
        <TournamentGridComponent
          hideMarketTypeHeaders
          hideOutcomeTypeHeaders
          marketTypeClassName="col"
          marketTypesPeriodMap={mainMarketTypesByPeriod}
          onEventClick={prematchEventClickHandler}
        />
      </TournamentLoader>
    </div>
  );
};

const ConnectedTournamentView = () => {
  return (
    <SelectedProviderWrapper Component={SelectedSportbookCategoryProvider}>
      <SelectedProviderWrapper Component={SelectedSportsbookTournamentProvider}>
        <TournamentRedirect>
          <div className="tournament-table tournament">
            <TournamentView />
          </div>
        </TournamentRedirect>
      </SelectedProviderWrapper>
    </SelectedProviderWrapper>
  );
};

export default ConnectedTournamentView;
